<template>
  <div class="safety-management-container" v-if="visible" @click.self="close">
    <div class="safety-management-modal" @click.stop>
      <!-- 头部 -->
      <div class="safety-header">
        <div class="header-title">
          <span class="title-icon">🏗️</span>
          <span class="title-text">黄河古贤工程施工安全管理AI系统</span>
        </div>
        <button class="close-btn" @click="close">×</button>
      </div>

      <!-- 内容区域 -->
      <div class="safety-content">
        <!-- 左侧：安全知识推送 -->
        <div class="safety-sidebar">
          <div class="safety-modules">
            <!-- 施工阶段选择 -->
            <div class="module-section">
              <div class="module-title">🔧 施工阶段</div>
              <div class="module-options">
                <div
                    v-for="stage in constructionStages"
                    :key="stage.id"
                    class="stage-option"
                    :class="{ active: selectedStage === stage.id }"
                    @click="selectStage(stage.id)"
                >
                  <span class="stage-icon">{{ stage.icon }}</span>
                  <span class="stage-name">{{ stage.name }}</span>
                </div>
              </div>
            </div>

            <!-- 工序类型选择 -->
            <div class="module-section">
              <div class="module-title">⚙️ 工序类型</div>
              <div class="module-options">
                <div
                    v-for="process in processTypes"
                    :key="process.id"
                    class="process-option"
                    :class="{ active: selectedProcess === process.id }"
                    @click="selectProcess(process.id)"
                >
                  <span class="process-name">{{ process.name }}</span>
                </div>
              </div>
            </div>

            <!-- 工程部位选择 -->
            <div class="module-section">
              <div class="module-title">📍 工程部位</div>
              <div class="module-options">
                <div
                    v-for="location in projectLocations"
                    :key="location.id"
                    class="location-option"
                    :class="{ active: selectedLocation === location.id }"
                    @click="selectLocation(location.id)"
                >
                  <span class="location-name">{{ location.name }}</span>
                </div>
              </div>
            </div>

            <!-- 主动推送 -->
            <div class="push-section" v-if="canPushSafetyInfo">
              <div class="push-header">
                <span class="push-icon">🔔</span>
                <span class="push-title">安全知识推送</span>
              </div>
              <button class="push-btn" @click="pushSafetyInfo">
                推送安全检查要点
              </button>
            </div>

            <!-- 历史查询 -->
            <div class="history-section">
              <div class="history-header">
                <span>历史查询</span>
                <button class="clear-btn" @click="clearHistory">清空</button>
              </div>
              <div class="history-list">
                <div
                    v-for="(item, index) in history"
                    :key="index"
                    class="history-item"
                    :class="{ active: activeHistory === index }"
                    @click="loadHistory(index)"
                >
                  <span class="history-icon">{{ item.icon }}</span>
                  <span class="history-text">{{ item.title }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：对话区域 -->
        <div class="chat-area">
          <!-- 消息列表 -->
          <div class="messages-container" ref="messagesContainer">
            <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
              <!-- 用户消息 -->
              <div v-if="message.role === 'user'" class="message user-message">
                <div class="message-avatar">👤</div>
                <div class="message-content">
                  <div class="message-text">{{ message.content }}</div>
                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>

              <!-- AI消息 -->
              <div v-if="message.role === 'assistant'" class="message assistant-message">
                <div class="message-avatar">🤖</div>
                <div class="message-content">
                  <!-- 安全检查卡片 -->
                  <div v-if="message.type === 'safety_check'" class="safety-card">
                    <div class="card-header">
                      <span class="card-title">📋 安全检查要点推送</span>
                      <span class="card-subtitle">根据施工阶段、工序、部位自动匹配</span>
                    </div>

                    <div class="safety-context">
                      <div class="context-info">
                        <span class="info-item">施工阶段：{{ message.data.stage }}</span>
                        <span class="info-item">工序类型：{{ message.data.process }}</span>
                        <span class="info-item">工程部位：{{ message.data.location }}</span>
                      </div>
                    </div>

                    <!-- 安全检查清单 -->
                    <div class="checklist-section" v-if="message.data.checklist.length > 0">
                      <div class="section-title">
                        <span class="section-icon">✅</span>
                        安全检查清单
                      </div>
                      <div class="checklist-content">
                        <div v-for="(item, idx) in message.data.checklist" :key="'check-'+idx" class="check-item">
                          <div class="check-header">
                            <input type="checkbox" class="check-input" :id="'check-'+idx">
                            <label :for="'check-'+idx" class="check-title">{{ item.title }}</label>
                            <span class="check-priority" :class="'priority-'+item.priority">{{ getPriorityText(item.priority) }}</span>
                          </div>
                          <div class="check-content">{{ item.content }}</div>
                          <div class="check-requirements" v-if="item.requirements">
                            <div class="req-title">具体要求：</div>
                            <ul class="req-list">
                              <li v-for="(req, reqIdx) in item.requirements" :key="'req-'+reqIdx">{{ req }}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 典型事故案例 -->
                    <div class="case-section" v-if="message.data.cases.length > 0">
                      <div class="section-title">
                        <span class="section-icon">⚠️</span>
                        典型事故案例
                      </div>
                      <div class="case-content">
                        <div v-for="(item, idx) in message.data.cases" :key="'case-'+idx" class="case-item">
                          <div class="case-header">
                            <div class="case-name-section">
                              <span class="case-name">{{ item.name }}</span>
                              <span class="case-location">（{{ item.location }}）</span>
                            </div>
                            <div class="case-tags">
                              <span class="case-level" :class="'level-'+item.level">{{ getAccidentLevel(item.level) }}</span>
                              <span class="case-date">{{ item.date }}</span>
                            </div>
                          </div>
                          <div class="case-desc">{{ item.description }}</div>
                          <div class="case-causes" v-if="item.causes">
                            <div class="causes-title">主要原因：</div>
                            <ul class="causes-list">
                              <li v-for="(cause, cIdx) in item.causes" :key="'cause-'+cIdx">{{ cause }}</li>
                            </ul>
                          </div>
                          <div class="case-lessons" v-if="item.lessons">
                            <div class="lessons-title">经验教训：</div>
                            <div class="lessons-content">{{ item.lessons }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 风险防控建议 -->
                    <div class="suggestion-section" v-if="message.data.suggestions">
                      <div class="section-title">
                        <span class="section-icon">💡</span>
                        风险防控建议
                      </div>
                      <div class="suggestion-content">
                        <div class="suggestion-text">{{ message.data.suggestions.summary }}</div>
                        <div class="suggestion-actions" v-if="message.data.suggestions.actions">
                          <div class="action-title">建议措施：</div>
                          <ul class="action-list">
                            <li v-for="(action, aIdx) in message.data.suggestions.actions" :key="'action-'+aIdx">{{ action }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 应急处置卡片 -->
                  <div v-else-if="message.type === 'emergency_response'" class="emergency-card">
                    <div class="card-header">
                      <span class="card-title">🚨 应急处置方案</span>
                      <span class="card-subtitle">针对异常工况的处置建议</span>
                    </div>

                    <!-- 工况描述 -->
                    <div class="situation-section">
                      <div class="section-title">
                        <span class="section-icon">🔍</span>
                        异常工况识别
                      </div>
                      <div class="situation-content">
                        <div class="situation-text">{{ message.data.situation }}</div>
                        <div class="risk-level" :class="'risk-'+message.data.riskLevel">
                          风险等级：{{ getRiskLevelText(message.data.riskLevel) }}
                        </div>
                      </div>
                    </div>

                    <!-- 类似历史场景 -->
                    <div class="history-scene" v-if="message.data.historyScenes.length > 0">
                      <div class="section-title">
                        <span class="section-icon">📊</span>
                        类似历史场景
                      </div>
                      <div class="scene-content">
                        <div v-for="(scene, idx) in message.data.historyScenes" :key="'scene-'+idx" class="scene-item">
                          <div class="scene-header">
                            <span class="scene-project">{{ scene.project }}</span>
                            <span class="scene-date">{{ scene.date }}</span>
                          </div>
                          <div class="scene-desc">{{ scene.description }}</div>
                          <div class="scene-result">
                            <span class="result-label">处置结果：</span>
                            <span class="result-text">{{ scene.result }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 处置方案 -->
                    <div class="response-plan" v-if="message.data.responsePlan">
                      <div class="section-title">
                        <span class="section-icon">📋</span>
                        处置方案
                      </div>
                      <div class="plan-content">
                        <div class="plan-step" v-for="(step, idx) in message.data.responsePlan.steps" :key="'step-'+idx">
                          <div class="step-header">
                            <span class="step-number">步骤 {{ idx + 1 }}</span>
                            <span class="step-time">预计耗时：{{ step.duration }}</span>
                          </div>
                          <div class="step-content">{{ step.content }}</div>
                          <div class="step-requirements" v-if="step.requirements">
                            <div class="step-req-title">要求：</div>
                            <ul class="step-req-list">
                              <li v-for="(req, rIdx) in step.requirements" :key="'req-'+rIdx">{{ req }}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 专家经验 -->
                    <div class="expert-advice" v-if="message.data.expertAdvice.length > 0">
                      <div class="section-title">
                        <span class="section-icon">👨‍💼</span>
                        专家经验建议
                      </div>
                      <div class="advice-content">
                        <div v-for="(advice, idx) in message.data.expertAdvice" :key="'advice-'+idx" class="advice-item">
                          <div class="advice-header">
                            <span class="advice-expert">{{ advice.expert }}</span>
                            <span class="advice-title">{{ advice.title }}</span>
                            <span class="advice-unit">（{{ advice.unit }}）</span>
                          </div>
                          <div class="advice-content">{{ advice.content }}</div>
                          <div class="advice-basis" v-if="advice.basis">
                            <div class="basis-title">依据：</div>
                            <div class="basis-content">{{ advice.basis }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 联系信息 -->
                    <div class="contact-info" v-if="message.data.contacts">
                      <div class="section-title">
                        <span class="section-icon">📞</span>
                        紧急联系人
                      </div>
                      <div class="contact-content">
                        <div v-for="(contact, idx) in message.data.contacts" :key="'contact-'+idx" class="contact-item">
                          <span class="contact-name">{{ contact.name }}</span>
                          <span class="contact-role">{{ contact.role }}</span>
                          <span class="contact-phone">{{ contact.phone }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 普通文本回复 -->
                  <div v-else class="message-text">{{ message.content }}</div>

                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>
            </div>
            <!-- 加载中 -->
            <div v-if="loading" class="loading-message">
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="loading-text">正在检索安全知识库、事故案例库...</span>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="input-area">
            <div class="input-tools">
              <button
                  v-for="(tool, idx) in inputTools"
                  :key="'tool-'+idx"
                  class="tool-btn"
                  :title="tool.title"
                  @click="useTool(tool.action)"
              >
                {{ tool.icon }}
              </button>
            </div>
            <div class="input-wrapper">
              <textarea
                  v-model="userInput"
                  placeholder="请输入安全相关问题，如：发现基坑局部渗漏应如何处置？"
                  @keydown.enter.exact.prevent="sendMessage"
                  @keydown.enter.shift.exact.prevent="userInput += '\n'"
                  rows="3"
                  class="message-input"
                  ref="messageInput"
              ></textarea>
              <button class="send-btn" @click="sendMessage" :disabled="!userInput.trim()">
                <span class="send-icon">↗</span>
              </button>
            </div>
            <div class="quick-actions">
              <span class="quick-label">快速查询：</span>
              <button
                  v-for="(action, idx) in quickActions"
                  :key="'action-'+idx"
                  class="quick-btn"
                  @click="quickQuery(action.query)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConstructionSafetyQA',
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    initialQuery: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: ''
    },
    smartOrbHandoff: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      userInput: '',
      lastInitialQuery: '',
      messages: [
        {
          role: 'user',
          content: '混凝土浇筑施工需要哪些安全检查要点？',
          time: '09:15'
        },
        {
          role: 'assistant',
          type: 'safety_check',
          content: '根据混凝土浇筑施工的安全检查要求，系统自动推送以下安全知识：',
          time: '09:16',
          data: {
            stage: '主体施工',
            process: '混凝土浇筑',
            location: '大坝主体',
            checklist: [
              {
                title: '脚手架与模板支撑检查',
                priority: 1,
                content: '检查脚手架和模板支撑系统的稳定性、连接件紧固情况',
                requirements: [
                  '立杆垂直度偏差≤1/200',
                  '横杆水平度偏差≤1/300',
                  '连接扣件扭力矩40-65N·m',
                  '支撑系统沉降监测每日记录'
                ]
              },
              {
                title: '混凝土输送设备安全检查',
                priority: 1,
                content: '检查泵车、布料机等设备的稳定性和安全装置',
                requirements: [
                  '泵车支腿必须完全展开并垫实',
                  '布料机回转半径内不得有人员停留',
                  '输送管道连接牢固无泄漏',
                  '急停装置功能正常'
                ]
              },
              {
                title: '临边防护与安全通道',
                priority: 2,
                content: '检查工作面的临边防护和安全通道设置',
                requirements: [
                  '临边设置1.2m高防护栏杆',
                  '安全通道宽度≥0.8m',
                  '通道防滑措施到位',
                  '危险区域设置警示标志'
                ]
              }
            ],
            cases: [
              {
                name: '某水利工程模板坍塌事故',
                location: '某水电站大坝',
                level: 2,
                date: '2020年8月',
                description: '混凝土浇筑过程中模板支撑系统失稳导致局部坍塌，造成3人受伤。',
                causes: [
                  '模板支撑立杆间距过大',
                  '水平连杆设置不足',
                  '浇筑速度过快产生过大侧压力',
                  '现场监测不到位'
                ],
                lessons: '必须严格控制模板支撑系统的设计和施工质量，加强浇筑过程的实时监测'
              }
            ],
            suggestions: {
              summary: '混凝土浇筑是高风险工序，需重点监控模板支撑、设备安全和临边防护。',
              actions: [
                '浇筑前必须进行全面的安全检查和技术交底',
                '严格控制浇筑速度和分层厚度',
                '安排专人进行全过程安全监护',
                '准备应急物资和应急预案'
              ]
            }
          }
        }
      ],
      history: [
        { icon: '⚠️', title: '基坑支护变形处置方案', time: '10:30' },
        { icon: '🚧', title: '高处作业安全检查要点', time: '昨天' },
        { icon: '💧', title: '渗漏水应急处置流程', time: '前天' },
        { icon: '🔥', title: '施工用电安全隐患排查', time: '2024-01-15' }
      ],
      activeHistory: null,
      loading: false,

      // 施工阶段
      selectedStage: 'main_construction',
      constructionStages: [
        { id: 'preparation', name: '施工准备', icon: '📋' },
        { id: 'foundation', name: '基础施工', icon: '🏗️' },
        { id: 'main_construction', name: '主体施工', icon: '🏢' },
        { id: 'finishing', name: '收尾阶段', icon: '🎯' }
      ],

      // 工序类型
      selectedProcess: 'concrete',
      processTypes: [
        { id: 'excavation', name: '土方开挖' },
        { id: 'support', name: '支护施工' },
        { id: 'concrete', name: '混凝土浇筑' },
        { id: 'reinforcement', name: '钢筋工程' },
        { id: 'formwork', name: '模板工程' },
        { id: 'high_work', name: '高处作业' },
        { id: 'hoisting', name: '起重吊装' },
        { id: 'blasting', name: '爆破作业' }
      ],

      // 工程部位
      selectedLocation: 'dam_body',
      projectLocations: [
        { id: 'dam_body', name: '大坝主体' },
        { id: 'powerhouse', name: '发电厂房' },
        { id: 'spillway', name: '泄洪建筑物' },
        { id: 'diversion', name: '导流工程' },
        { id: 'foundation_pit', name: '基坑' },
        { id: 'slope', name: '边坡' },
        { id: 'tunnel', name: '隧洞' },
        { id: 'access_road', name: '施工道路' }
      ],

      inputTools: [
        { icon: '📸', title: '拍照上传', action: 'upload_photo' },
        { icon: '📍', title: '位置标注', action: 'location_mark' },
        { icon: '🔍', title: '隐患识别', action: 'hazard_scan' },
        { icon: '📄', title: '生成检查单', action: 'generate_checklist' },
        { icon: '🚨', title: '一键报警', action: 'emergency_call' }
      ],

      quickActions: [
        { label: '隐患识别', query: '请识别当前施工中的安全隐患' },
        { label: '检查要点', query: '推送当前工序安全检查要点' },
        { label: '事故案例', query: '查看类似事故案例' },
        { label: '应急处置', query: '异常工况应急处置方案' },
        { label: '规程规范', query: '相关安全规程要求' }
      ],

      // 历史回答映射
      historyAnswerMap: {
        '基坑支护变形处置方案': this.generateSlopeResponse,
        '高处作业安全检查要点': this.generateHighWorkResponse,
        '渗漏水应急处置流程': this.generateWaterSeepageResponse,
        '施工用电安全隐患排查': this.generateElectricityResponse
      }
    }
  },
  computed: {
    canPushSafetyInfo() {
      return this.selectedStage && this.selectedProcess && this.selectedLocation
    },

    getStageName() {
      const stage = this.constructionStages.find(s => s.id === this.selectedStage)
      return stage ? stage.name : ''
    },

    getProcessName() {
      const process = this.processTypes.find(p => p.id === this.selectedProcess)
      return process ? process.name : ''
    },

    getLocationName() {
      const location = this.projectLocations.find(l => l.id === this.selectedLocation)
      return location ? location.name : ''
    }
  },
  watch: {
    initialQuery: {
      immediate: true,
      handler(val) {
        const query = String(val || '').trim()
        if (!query || query === this.lastInitialQuery) return
        this.lastInitialQuery = query
        this.$nextTick(() => {
          this.userInput = query
          this.sendMessage()
        })
      }
    },
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToBottom()
          this.$refs.messageInput?.focus()
        })
      }
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  },
  methods: {
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    },

    getPriorityText(priority) {
      const texts = ['低', '中', '高']
      return texts[priority - 1] || '中'
    },

    getAccidentLevel(level) {
      const levels = ['一般', '较大', '重大', '特大']
      return levels[level - 1] || '一般'
    },

    getRiskLevelText(level) {
      const levels = { 1: '低风险', 2: '中风险', 3: '高风险', 4: '重大风险' }
      return levels[level] || '未知'
    },

    selectStage(stageId) {
      this.selectedStage = stageId
    },

    selectProcess(processId) {
      this.selectedProcess = processId
    },

    selectLocation(locationId) {
      this.selectedLocation = locationId
    },

    pushSafetyInfo() {
      if (!this.canPushSafetyInfo) return

      this.loading = true

      // 模拟推送安全检查要点
      setTimeout(() => {
        this.generateSafetyCheckResponse()
        this.loading = false
        this.scrollToBottom()
      }, 1000)
    },

    async sendMessage() {
      const input = this.userInput.trim()
      if (!input) return

      this.messages.push({
        role: 'user',
        content: input,
        time: this.getCurrentTime()
      })

      this.userInput = ''
      this.loading = true

      this.addToHistory(input)

      setTimeout(() => {
        this.generateProfessionalResponse(input)
        this.loading = false
        this.scrollToBottom()
      }, 1500)
    },

    generateProfessionalResponse(query) {
      const responses = {
        '隐患': this.generateHazardResponse,
        '检查': this.generateSafetyCheckResponse,
        '事故': this.generateAccidentResponse,
        '处置': this.generateEmergencyResponse,
        '应急': this.generateEmergencyResponse,
        '规程': this.generateRegulationResponse,
        '规范': this.generateRegulationResponse,
        '基坑': this.generateSlopeResponse,
        '渗漏': this.generateWaterSeepageResponse,
        '裂缝': this.generateWaterSeepageResponse,
        '变形': this.generateSlopeResponse,
        '高处作业': this.generateHighWorkResponse,
        '用电': this.generateElectricityResponse,
        '支护': this.generateSlopeResponse
      }

      let responseGenerator = this.generateGeneralResponse

      for (const [keyword, generator] of Object.entries(responses)) {
        if (query.includes(keyword)) {
          responseGenerator = generator
          break
        }
      }

      const response = responseGenerator(query)
      this.messages.push(response)
    },

    generateSafetyCheckResponse() {
      return {
        role: 'assistant',
        type: 'safety_check',
        content: '根据您当前施工阶段、工序和部位，系统推送以下安全知识：',
        time: this.getCurrentTime(),
        data: {
          stage: this.getStageName,
          process: this.getProcessName,
          location: this.getLocationName,
          checklist: [
            {
              title: '现场安全防护检查',
              priority: 1,
              content: '检查工作面的安全防护设施是否齐全有效',
              requirements: [
                '安全帽、安全带等个人防护用品佩戴规范',
                '临边、洞口防护措施到位',
                '安全警示标志齐全醒目',
                '消防器材配置合理有效'
              ]
            },
            {
              title: '机械设备安全检查',
              priority: 2,
              content: '检查施工机械设备的运行状态和安全装置',
              requirements: [
                '设备定期检验合格证齐全',
                '安全限位装置功能正常',
                '操作人员持证上岗',
                '设备运行记录完整'
              ]
            },
            {
              title: '施工用电安全检查',
              priority: 2,
              content: '检查施工现场用电安全情况',
              requirements: [
                '三级配电两级保护落实到位',
                '电缆线路敷设规范无破损',
                '漏电保护器灵敏可靠',
                '用电设备接地接零良好'
              ]
            }
          ],
          cases: [
            {
              name: '某工程高处坠落事故',
              location: '某水电站厂房',
              level: 2,
              date: '2021年5月',
              description: '作业人员在临边作业时未系安全带，失足坠落造成重伤。',
              causes: [
                '个人防护用品使用不规范',
                '临边防护措施不到位',
                '安全监护缺失',
                '安全教育培训不足'
              ],
              lessons: '必须严格执行高处作业安全规程，加强个人防护用品使用监管'
            }
          ],
          suggestions: {
            summary: '安全是施工的前提，必须时刻保持警惕，严格执行安全规程。',
            actions: [
              '每日开工前进行安全交底',
              '加强现场安全巡查和监护',
              '及时整改安全隐患',
              '完善应急预案和演练'
            ]
          }
        }
      }
    },

    generateEmergencyResponse(query) {
      return {
        role: 'assistant',
        type: 'emergency_response',
        content: '针对您提到的异常工况，系统提供以下应急处置方案：',
        time: this.getCurrentTime(),
        data: {
          situation: query,
          riskLevel: 3,
          historyScenes: [
            {
              project: '某抽水蓄能电站',
              date: '2022年3月',
              description: '地下厂房开挖过程中出现围岩局部变形，最大变形量达5cm',
              result: '立即停止开挖，采取临时支撑和注浆加固，成功控制变形发展'
            },
            {
              project: '某水库工程',
              date: '2021年8月',
              description: '汛期基坑出现涌水涌砂，影响基坑稳定',
              result: '启动应急预案，采取井点降水和边坡加固措施，保障基坑安全'
            }
          ],
          responsePlan: {
            steps: [
              {
                duration: '立即',
                content: '立即停止相关作业，疏散危险区域人员',
                requirements: [
                  '设置警戒区域',
                  '禁止无关人员进入',
                  '通知现场负责人'
                ]
              },
              {
                duration: '15分钟内',
                content: '组织技术人员现场勘查，评估风险等级',
                requirements: [
                  '测量变形、渗漏等参数',
                  '拍照记录现场情况',
                  '初步判断原因'
                ]
              },
              {
                duration: '1小时内',
                content: '制定应急处置方案，调集应急物资和设备',
                requirements: [
                  '方案需经技术负责人审批',
                  '检查应急物资储备',
                  '组织应急队伍'
                ]
              }
            ]
          },
          expertAdvice: [
            {
              expert: '王安全总监',
              title: '安全工程专家',
              unit: '黄河水利委员会',
              content: '应急处置的关键是快速响应、科学决策、有效控制。',
              basis: '20余年水利工程安全管理经验',
              recommendations: [
                '建立完善的应急响应机制',
                '加强应急演练和培训',
                '储备充足的应急物资',
                '建立专家支持系统'
              ]
            }
          ],
          contacts: [
            { name: '张工', role: '安全总监', phone: '138****1234' },
            { name: '李工', role: '技术负责人', phone: '139****5678' },
            { name: '王工', role: '应急救援队长', phone: '137****9012' }
          ]
        }
      }
    },

    generateSlopeResponse() {
      return this.generateEmergencyResponse('基坑支护变形')
    },

    generateWaterSeepageResponse() {
      return this.generateEmergencyResponse('渗漏水问题')
    },

    generateHighWorkResponse() {
      return this.generateSafetyCheckResponse()
    },

    generateElectricityResponse() {
      return this.generateGeneralResponse('施工用电安全')
    },

    generateHazardResponse() {
      return this.generateGeneralResponse('隐患识别')
    },

    generateAccidentResponse() {
      return this.generateGeneralResponse('事故案例分析')
    },

    generateRegulationResponse() {
      return this.generateGeneralResponse('安全规程要求')
    },

    generateGeneralResponse(query) {
      return {
        role: 'assistant',
        content: `关于"${query}"，系统已从安全知识库中检索相关信息。施工安全管理需严格执行相关规程规范，加强现场检查，及时消除隐患。您是否需要更详细的安全检查要点或应急处置方案？`,
        time: this.getCurrentTime()
      }
    },

    addToHistory(query) {
      const icons = ['⚠️', '🚧', '💧', '🔥', '⚡', '🏗️']
      const randomIcon = icons[Math.floor(Math.random() * icons.length)]
      const title = query.length > 20 ? query.substring(0, 20) + '...' : query

      this.history.unshift({
        icon: randomIcon,
        title: title,
        time: this.getCurrentTime()
      })

      if (this.history.length > 10) {
        this.history.pop()
      }
    },

    loadHistory(index) {
      this.activeHistory = index
      const historyItem = this.history[index]

      this.messages = []

      this.messages.push({
        role: 'user',
        content: historyItem.title,
        time: historyItem.time
      })

      const answerGenerator = this.historyAnswerMap[historyItem.title] || this.generateGeneralResponse
      const response = answerGenerator.call(this)
      response.time = this.getCurrentTime()
      this.messages.push(response)

      this.scrollToBottom()
    },

    clearHistory() {
      this.history = []
    },

    quickQuery(query) {
      this.userInput = query
      this.sendMessage()
    },

    useTool(action) {
      const toolActions = {
        upload_photo: () => {
          this.messages.push({
            role: 'assistant',
            content: '已启用拍照上传功能，可用于记录安全隐患、不规范操作等。',
            time: this.getCurrentTime()
          })
        },
        location_mark: () => {
          this.messages.push({
            role: 'assistant',
            content: '位置标注功能已启用，可在地图上标记隐患位置、施工区域等。',
            time: this.getCurrentTime()
          })
        },
        hazard_scan: () => {
          this.messages.push({
            role: 'assistant',
            content: '隐患识别功能启动，系统将自动分析现场照片中的安全隐患。',
            time: this.getCurrentTime()
          })
        },
        generate_checklist: () => {
          this.messages.push({
            role: 'assistant',
            content: '正在根据当前施工情况生成安全检查清单...',
            time: this.getCurrentTime()
          })
        },
        emergency_call: () => {
          this.messages.push({
            role: 'assistant',
            content: '⚠️ 紧急报警功能已触发，已通知现场安全负责人和应急救援队！',
            time: this.getCurrentTime()
          })
        }
      }

      if (toolActions[action]) {
        toolActions[action]()
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        this.$nextTick(() => {
          container.scrollTop = container.scrollHeight
        })
      }
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.safety-management-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.safety-management-modal {
  width: 1200px;
  height: 700px;
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  border-radius: 12px;
  border: 1px solid rgba(255, 193, 7, 0.3);
  box-shadow: 0 0 30px rgba(255, 193, 7, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 头部样式 */
.safety-header {
  height: 60px;
  background: linear-gradient(90deg, #1a237e 0%, #283593 100%);
  border-bottom: 1px solid rgba(255, 193, 7, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.title-text {
  font-size: 18px;
  font-weight: bold;
  color: #ffc107;
  text-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
}

.close-btn {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  color: #ffc107;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 193, 7, 0.2);
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
  transform: rotate(90deg);
}

/* 内容区域 */
.safety-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧安全知识推送 */
.safety-sidebar {
  width: 300px;
  background: rgba(30, 40, 120, 0.6);
  border-right: 1px solid rgba(255, 193, 7, 0.2);
  display: flex;
  flex-direction: column;
  min-width: 300px;
  padding: 15px;
  gap: 20px;
  overflow-y: auto;
}

.safety-modules {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.module-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 193, 7, 0.1);
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s;
}

.module-section:hover {
  border-color: rgba(255, 193, 7, 0.3);
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.1);
}

.module-title {
  color: #ffc107;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.module-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stage-option,
.process-option,
.location-option {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e0e0e0;
}

.stage-option:hover,
.process-option:hover,
.location-option:hover {
  background: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.3);
  transform: translateX(5px);
}

.stage-option.active,
.process-option.active,
.location-option.active {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 8px rgba(255, 193, 7, 0.3);
}

.stage-icon {
  font-size: 20px;
}

.push-section {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1), rgba(255, 152, 0, 0.1));
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-top: 10px;
}

.push-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #ffc107;
  font-weight: bold;
}

.push-icon {
  font-size: 20px;
}

.push-btn {
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, #ffc107, #ff9800);
  border: none;
  border-radius: 6px;
  color: #1a237e;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.push-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 193, 7, 0.4);
}

.push-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.history-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 193, 7, 0.1);
  border-radius: 8px;
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #ffc107;
  font-weight: bold;
  font-size: 16px;
}

.clear-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffc107;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.clear-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 193, 7, 0.5);
}

.history-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  min-height: 50px;
}

.history-item:hover {
  background: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.3);
  transform: translateX(5px);
}

.history-item.active {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 8px rgba(255, 193, 7, 0.3);
}

.history-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.history-text {
  flex: 1;
  color: #e0e0e0;
  font-size: 13px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 右侧对话区域 */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(10, 15, 40, 0.3);
}

.messages-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: messageAppear 0.3s ease;
}

@keyframes messageAppear {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.message {
  display: flex;
  gap: 12px;
  max-width: 80%;
  align-items: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.assistant-message {
  align-self: flex-start;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3949ab, #283593);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.user-message .message-avatar {
  background: linear-gradient(135deg, #5c6bc0, #3949ab);
}

.message-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.user-message .message-content {
  align-items: flex-end;
}

.assistant-message .message-content {
  align-items: flex-start;
}

.user-message .message-text {
  background: linear-gradient(135deg, rgba(92, 107, 192, 0.8), rgba(57, 73, 171, 0.8));
  border: 1px solid rgba(92, 107, 192, 0.5);
  color: #fff;
  border-radius: 12px 12px 0 12px;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.assistant-message .message-text {
  background: linear-gradient(135deg, rgba(57, 73, 171, 0.8), rgba(40, 53, 147, 0.8));
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #e0f7fa;
  border-radius: 12px 12px 12px 0;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.message-time {
  font-size: 12px;
  color: #8c9eb5;
  margin-top: 4px;
}

/* 安全检查卡片样式 */
.safety-card,
.emergency-card {
  background: rgba(30, 40, 120, 0.8);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  margin-top: 8px;
  width: 100%;
  animation: cardAppear 0.5s ease;
}

@keyframes cardAppear {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.card-header {
  padding: 15px 20px;
  background: linear-gradient(90deg, #1a237e, #283593);
  border-bottom: 1px solid rgba(255, 193, 7, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.card-title {
  color: #ffc107;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-subtitle {
  color: #8c9eb5;
  font-size: 12px;
  font-style: italic;
}

.safety-context {
  padding: 15px 20px;
  background: rgba(255, 193, 7, 0.1);
  border-bottom: 1px solid rgba(255, 193, 7, 0.2);
}

.context-info {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.info-item {
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: 4px;
  color: #ffc107;
  font-size: 13px;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

/* 卡片部分通用样式 */
.checklist-section,
.case-section,
.suggestion-section,
.situation-section,
.history-scene,
.response-plan,
.expert-advice,
.contact-info {
  padding: 15px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}

.checklist-section:hover,
.case-section:hover,
.suggestion-section:hover,
.situation-section:hover,
.history-scene:hover,
.response-plan:hover,
.expert-advice:hover,
.contact-info:hover {
  background: rgba(255, 255, 255, 0.05);
}

.section-title {
  color: #ffc107;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-icon {
  font-size: 16px;
  margin-right: 6px;
}

/* 检查清单样式 */
.checklist-content,
.case-content,
.scene-content,
.plan-content,
.advice-content,
.contact-content {
  color: #e0e0e0;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.check-item {
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border-left: 3px solid #4caf50;
  transition: all 0.3s;
  animation: itemAppear 0.3s ease;
}

@keyframes itemAppear {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
}

.check-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.check-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.check-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.check-title {
  flex: 1;
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
  cursor: pointer;
}

.check-priority {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.priority-1 { background: rgba(76, 175, 80, 0.2); color: #4caf50; }
.priority-2 { background: rgba(255, 193, 7, 0.2); color: #ffc107; }
.priority-3 { background: rgba(244, 67, 54, 0.2); color: #f44336; }

.check-content {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.check-requirements {
  margin-top: 8px;
  padding: 8px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 4px;
}

.req-title {
  color: #4caf50;
  font-size: 12px;
  margin-bottom: 4px;
  font-weight: 500;
}

.req-list {
  margin: 4px 0 4px 16px;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.5;
}

.req-list li {
  margin-bottom: 2px;
}

/* 事故案例样式 */
.case-item {
  padding: 12px;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 8px;
  border-left: 3px solid #f44336;
  transition: all 0.3s;
}

.case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.case-name-section {
  display: flex;
  align-items: center;
  gap: 4px;
}

.case-name {
  color: #f44336;
  font-weight: bold;
  font-size: 13px;
}

.case-location {
  color: #8c9eb5;
  font-size: 12px;
}

.case-tags {
  display: flex;
  gap: 8px;
  align-items: center;
}

.case-level {
  background: rgba(244, 67, 54, 0.2);
  color: #f44336;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.level-1 { background: rgba(76, 175, 80, 0.2); color: #4caf50; border-color: rgba(76, 175, 80, 0.3); }
.level-2 { background: rgba(255, 193, 7, 0.2); color: #ffc107; border-color: rgba(255, 193, 7, 0.3); }
.level-3 { background: rgba(244, 67, 54, 0.2); color: #f44336; border-color: rgba(244, 67, 54, 0.3); }
.level-4 { background: rgba(103, 58, 183, 0.2); color: #673ab7; border-color: rgba(103, 58, 183, 0.3); }

.case-date {
  color: #8c9eb5;
  font-size: 12px;
}

.case-desc {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.case-causes {
  margin: 8px 0;
  padding: 8px;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 4px;
}

.causes-title {
  color: #f44336;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}

.causes-list {
  margin: 4px 0 4px 16px;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.5;
}

.case-lessons {
  margin-top: 8px;
  padding: 8px;
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
  border-radius: 4px;
}

.lessons-title {
  color: #ffc107;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}

.lessons-content {
  color: #ffeaa7;
  font-size: 12px;
  line-height: 1.4;
}

/* 风险防控建议样式 */
.suggestion-content {
  margin: 8px 0;
  padding: 12px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 6px;
}

.suggestion-text {
  margin-bottom: 8px;
  line-height: 1.5;
  color: #e0f7fa;
  font-size: 13px;
}

.suggestion-actions {
  padding: 8px;
  background: rgba(0, 150, 255, 0.1);
  border-radius: 6px;
  margin-top: 8px;
}

.action-title {
  color: #4fc3f7;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 6px;
}

.action-list {
  margin: 0 0 0 16px;
  color: #e0f7fa;
  font-size: 12px;
  line-height: 1.5;
}

.action-list li {
  margin-bottom: 4px;
}

/* 应急处置卡片特殊样式 */
.situation-content {
  padding: 12px;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 6px;
  margin-top: 8px;
}

.situation-text {
  margin-bottom: 8px;
  line-height: 1.5;
  color: #e0f7fa;
  font-size: 13px;
}

.risk-level {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  margin-top: 8px;
}

.risk-1 { background: rgba(76, 175, 80, 0.2); color: #4caf50; }
.risk-2 { background: rgba(255, 193, 7, 0.2); color: #ffc107; }
.risk-3 { background: rgba(255, 152, 0, 0.2); color: #ff9800; }
.risk-4 { background: rgba(244, 67, 54, 0.2); color: #f44336; }

/* 历史场景样式 */
.scene-item {
  padding: 12px;
  background: rgba(30, 40, 120, 0.5);
  border-radius: 8px;
  border-left: 3px solid #3f51b5;
  transition: all 0.3s;
}

.scene-item:hover {
  background: rgba(30, 40, 120, 0.7);
  transform: translateX(5px);
}

.scene-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.scene-project {
  color: #7986cb;
  font-weight: bold;
  font-size: 13px;
}

.scene-date {
  color: #8c9eb5;
  font-size: 12px;
}

.scene-desc {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.scene-result {
  padding: 8px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 4px;
  font-size: 12px;
}

.result-label {
  color: #4caf50;
  font-weight: 500;
}

.result-text {
  color: #e0e0e0;
}

/* 处置方案样式 */
.plan-step {
  padding: 12px;
  background: rgba(0, 150, 255, 0.1);
  border-radius: 8px;
  border-left: 3px solid #2196f3;
  margin-bottom: 12px;
  transition: all 0.3s;
}

.plan-step:hover {
  background: rgba(0, 150, 255, 0.2);
  transform: translateX(5px);
}

.step-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.step-number {
  color: #2196f3;
  font-weight: bold;
  font-size: 13px;
}

.step-time {
  color: #8c9eb5;
  font-size: 12px;
}

.step-content {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.step-requirements {
  margin-top: 8px;
  padding: 8px;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 4px;
}

.step-req-title {
  color: #2196f3;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}

.step-req-list {
  margin: 4px 0 4px 16px;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.5;
}

/* 专家建议样式 */
.advice-item {
  padding: 12px;
  background: rgba(156, 39, 176, 0.1);
  border-radius: 8px;
  border-left: 3px solid #9c27b0;
  transition: all 0.3s;
}

.advice-item:hover {
  background: rgba(156, 39, 176, 0.2);
  transform: translateX(5px);
}

.advice-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.advice-expert {
  color: #9c27b0;
  font-weight: bold;
  font-size: 13px;
}

.advice-title {
  color: #8c9eb5;
  font-size: 12px;
  font-style: italic;
}

.advice-unit {
  color: #8c9eb5;
  font-size: 11px;
  font-style: italic;
}

.advice-content {
  margin: 6px 0;
  line-height: 1.5;
  font-size: 12px;
  color: #e0e0e0;
}

.advice-basis {
  margin: 6px 0;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  font-size: 12px;
}

.basis-title {
  color: #8c9eb5;
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 2px;
}

.basis-content {
  color: #e0e0e0;
  font-style: italic;
}

/* 联系人样式 */
.contact-item {
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  transition: all 0.3s;
}

.contact-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(5px);
}

.contact-name {
  color: #4fc3f7;
  font-weight: bold;
  font-size: 13px;
  min-width: 80px;
}

.contact-role {
  color: #8c9eb5;
  font-size: 12px;
  flex: 1;
}

.contact-phone {
  color: #4caf50;
  font-size: 12px;
  font-weight: bold;
  background: rgba(76, 175, 80, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

/* 加载中 */
.loading-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  justify-content: center;
  color: #ffc107;
  font-size: 14px;
  align-self: center;
  width: 100%;
  text-align: center;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffc107;
  animation: dotFlashing 1s infinite alternate;
  box-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotFlashing {
  0% { opacity: 0.3; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1.2); }
}

.loading-text {
  color: #ffc107;
  font-size: 14px;
  text-shadow: 0 0 8px rgba(255, 193, 7, 0.5);
}

/* 输入区域 */
.input-area {
  padding: 15px 20px;
  background: rgba(30, 40, 120, 0.7);
  border-top: 1px solid rgba(255, 193, 7, 0.3);
  backdrop-filter: blur(10px);
}

.input-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.tool-btn {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  color: #ffc107;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.tool-btn:hover {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 8px rgba(255, 193, 7, 0.4);
  transform: translateY(-2px);
}

.input-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.message-input {
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  padding: 12px 50px 12px 15px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  resize: none;
  outline: none;
  transition: all 0.3s;
  line-height: 1.5;
  font-family: inherit;
}

.message-input:focus {
  border-color: #ffc107;
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
  background: rgba(255, 255, 255, 0.15);
}

.message-input::placeholder {
  color: #8c9eb5;
  opacity: 0.7;
}

.send-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #ffc107, #ff9800);
  border: none;
  border-radius: 50%;
  color: #1a237e;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ffb300, #f57c00);
  box-shadow: 0 0 15px rgba(255, 193, 7, 0.5);
  transform: translateY(-2px) scale(1.1);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.send-icon {
  font-size: 20px;
  font-weight: bold;
  transition: all 0.3s;
}

.quick-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-label {
  color: #ffc107;
  font-size: 12px;
  white-space: nowrap;
}

.quick-btn {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 16px;
  color: #e0f7fa;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.quick-btn:hover {
  background: rgba(255, 193, 7, 0.2);
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 8px rgba(255, 193, 7, 0.4);
  transform: translateY(-1px);
}
</style>
